import { useCallback, useEffect, useRef } from "react";

/**
 * Rows that join a list after mount grow in; rows present at mount do not
 * replay an arrival. Keys are the row identity, so a refetch that keeps the
 * same ids animates nothing.
 *
 * `ready` is the entrance-policy gate: while `false` the list is a skeleton
 * or a placeholder, so nothing is known and nothing is new. The first
 * render with `ready` true records the rows that arrived with the data and
 * animates none of them; only a later join is new.
 */
export function useNewRowDetector(keys: readonly string[], ready = true): (key: string) => boolean {
  const known = useRef<ReadonlySet<string> | null>(null);
  const snapshot = ready ? known.current : null;
  useEffect(() => {
    known.current = ready ? new Set(keys) : null;
  }, [keys, ready]);
  return useCallback((key: string) => snapshot !== null && !snapshot.has(key), [snapshot]);
}
